// src/components/layout/FileUploader.js
//
// Drop zone + file picker with upload progress (audit FL-09, FL-10, FL-11, FL-39).
//
// `onUpload(file, onProgress, signal)` performs the request (see utils/upload.js) and
// reports progress with `onProgress(percent, {loaded, total, rate, estimated})`.
// While a file uploads the progress is shown even when an older file exists
// (replacing a file), with speed, remaining time and a cancel button.
import React, { useRef, useState } from 'react';
import { Upload, Check, Loader, AlertCircle, X } from 'lucide-react';
import useUpload from '../../hooks/useUpload';
import { formatBytes, formatEta } from '../../utils/upload';
import { sessionEndsWithin, SESSION_ENDING_MESSAGE } from '../../utils/session';

const MIME_TO_EXTENSION = {
  'video/mp4': 'mp4',
  'video/quicktime': 'mov',
  'video/x-msvideo': 'avi',
  'audio/mpeg': 'mp3',
  'audio/mp3': 'mp3',
  'image/png': 'png',
  'image/jpeg': 'jpg',
};

const LARGE_FILE_BYTES = 200 * 1024 * 1024;
const TWO_HOURS = 2 * 60 * 60 * 1000;
const TEN_MINUTES = 10 * 60 * 1000;

/** Accepted extensions from an `accept` string like "audio/mpeg,.mp3". */
export function acceptedExtensions(accept) {
  if (!accept) return [];
  const list = accept
    .split(',')
    .map((type) => type.trim().toLowerCase())
    .filter(Boolean)
    .map((type) => (type.includes('/') ? MIME_TO_EXTENSION[type] || type.split('/')[1] : type.replace(/^\.+/, '')));
  return Array.from(new Set(list));
}

const sizeLimitLabel = (maxSizeMB) =>
  maxSizeMB >= 1024 ? `${(maxSizeMB / 1024).toLocaleString('cs-CZ')} GB` : `${maxSizeMB} MB`;

/**
 * Czech reason why `file` cannot be uploaded, or null. Extensions are case-insensitive.
 * `tooLargeHint` replaces the generic advice for a too large video (e.g. the Grand Finále
 * background video, which must keep its resolution).
 */
export function validateFile(file, { accept, maxSizeMB, tooLargeHint }) {
  const extensions = acceptedExtensions(accept);
  const name = (file.name || '').toLowerCase();
  const extension = name.includes('.') ? name.split('.').pop() : '';
  if (extensions.length > 0 && !extensions.includes(extension)) {
    return `Tento typ souboru nelze nahrát. Povolené formáty: ${extensions.map((e) => e.toUpperCase()).join(', ')}.`;
  }
  if (maxSizeMB && file.size > maxSizeMB * 1024 * 1024) {
    const isVideo = extensions.some((e) => ['mp4', 'mov', 'avi'].includes(e));
    const base = `Soubor je příliš velký (${formatBytes(file.size)}). Maximální velikost je ${sizeLimitLabel(maxSizeMB)}.`;
    if (tooLargeHint) return `${base} ${tooLargeHint}`;
    if (isVideo) {
      return (
        `${base} Stačí krátká ukázka v rozlišení 720p nebo 1080p – video zkraťte nebo zkomprimujte ` +
        '(např. v aplikaci HandBrake), případně ho z mobilu uložte v nižší kvalitě.'
      );
    }
    return base;
  }
  return null;
}

const FileUploader = ({
  onUpload,
  accept,
  maxSizeMB = 100,
  title,
  description,
  buttonText = 'Nahrát soubor',
  changeButtonText = 'Nahrát jiný soubor',
  successText = 'Soubor je nahraný',
  currentFile = null,
  error = null,
  disabled = false,
  label,
  id = 'file-upload',
  tooLargeHint,
}) => {
  const inputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const upload = useUpload({ label: label || title });
  const isBusy = upload.busy;
  const inputDisabled = disabled || isBusy;
  const extensions = acceptedExtensions(accept);

  const handleFile = (file) => {
    if (!file || inputDisabled) return;
    const problem = validateFile(file, { accept, maxSizeMB, tooLargeHint });
    if (problem) {
      upload.fail(problem, file.name);
      return;
    }
    const minRemaining = file.size > LARGE_FILE_BYTES ? TWO_HOURS : TEN_MINUTES;
    if (sessionEndsWithin(minRemaining)) {
      upload.fail(SESSION_ENDING_MESSAGE, file.name);
      return;
    }
    upload.start(file, (onProgress, signal) => onUpload(file, onProgress, signal));
  };

  const handleChange = (e) => {
    const file = e.target.files && e.target.files[0];
    // Reset so that choosing the same (fixed) file again fires onChange.
    e.target.value = '';
    handleFile(file);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (inputDisabled) return;
    if (e.type === 'dragenter' || e.type === 'dragover') setIsDragging(true);
    else if (e.type === 'dragleave') setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const file = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
    handleFile(file);
  };

  const openPicker = () => {
    if (!inputDisabled && inputRef.current) inputRef.current.click();
  };

  const speed = upload.rate ? `${formatBytes(upload.rate)}/s` : '';
  const eta = formatEta(upload.eta);

  return (
    <div className="w-full">
      {title && <h3 className="mb-1 text-sm font-medium text-gray-900">{title}</h3>}
      {description && <p className="mb-2 text-sm text-gray-600">{description}</p>}
      {error && (
        <p role="alert" className="mb-2 flex items-start gap-2 rounded-md bg-red-50 p-3 text-sm text-red-800">
          <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
          <span>{error}</span>
        </p>
      )}

      <div
        className={`relative rounded-lg border-2 border-dashed p-4 transition-colors sm:p-6 ${
          isDragging ? 'border-indigo-500 bg-indigo-50' : 'border-gray-300'
        } ${isBusy ? 'bg-gray-50' : ''}`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <input
          ref={inputRef}
          type="file"
          accept={accept || undefined}
          onChange={handleChange}
          className="hidden"
          id={id}
          disabled={inputDisabled}
          aria-label={currentFile ? changeButtonText : buttonText}
        />

        {isBusy ? (
          <div className="space-y-3">
            <div className="flex items-start gap-2 text-sm text-gray-700">
              <Loader className="mt-0.5 h-4 w-4 flex-shrink-0 animate-spin text-indigo-600" aria-hidden="true" />
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium">{upload.fileName}</p>
                <p className="text-gray-600">
                  {upload.status === 'processing'
                    ? 'Soubor je nahraný, server ho zpracovává…'
                    : `Nahrávám… ${formatBytes(upload.loaded)} z ${formatBytes(upload.total)}`}
                </p>
              </div>
              <span className="font-semibold tabular-nums text-gray-900">{upload.progress} %</span>
            </div>
            <div
              className="h-2 w-full overflow-hidden rounded-full bg-gray-200"
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={upload.progress}
              aria-label={`Průběh nahrávání souboru ${upload.fileName}`}
            >
              <div
                className="h-2 rounded-full bg-indigo-600 transition-all duration-300"
                style={{ width: `${upload.progress}%` }}
              />
            </div>
            <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-gray-600">
              <span>{[speed, eta].filter(Boolean).join(' · ')}</span>
              {upload.status === 'uploading' && (
                <button
                  type="button"
                  onClick={upload.cancel}
                  className="inline-flex items-center gap-1 rounded-md px-2 py-1 font-medium text-gray-700 hover:bg-gray-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                >
                  <X className="h-3.5 w-3.5" aria-hidden="true" />
                  Zrušit nahrávání
                </button>
              )}
            </div>
            <p className="text-xs text-gray-600">
              Nezavírejte stránku a nezamykejte telefon, dokud nahrávání neskončí. Na jinou stránku aplikace přejít můžete –
              průběh uvidíte v horní liště.
            </p>
          </div>
        ) : currentFile ? (
          <div className="flex flex-col items-center gap-3 text-center sm:flex-row sm:justify-between sm:text-left">
            <div className="flex items-center gap-2 text-sm text-gray-700">
              <Check className="h-5 w-5 flex-shrink-0 text-green-600" aria-hidden="true" />
              <span>{successText}</span>
            </div>
            {!disabled && (
              <button
                type="button"
                onClick={openPicker}
                className="inline-flex items-center justify-center gap-2 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
              >
                <Upload className="h-4 w-4" aria-hidden="true" />
                {changeButtonText}
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-2 text-center">
            <Upload className="mx-auto h-10 w-10 text-gray-400" aria-hidden="true" />
            <button
              type="button"
              onClick={openPicker}
              disabled={disabled}
              className="inline-flex items-center justify-center gap-2 rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {buttonText}
            </button>
            <p className="hidden text-sm text-gray-600 sm:block">nebo sem soubor přetáhněte</p>
            <p className="text-xs text-gray-600">
              {extensions.length > 0 && `${extensions.map((e) => e.toUpperCase()).join(', ')} · `}
              max. {sizeLimitLabel(maxSizeMB)}
            </p>
          </div>
        )}

        {upload.status === 'error' && upload.error && (
          <p role="alert" className="mt-3 flex items-start gap-2 rounded-md bg-red-50 p-3 text-sm text-red-800">
            <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
            <span>{upload.error}</span>
          </p>
        )}
        {upload.status === 'success' && (
          <p role="status" className="mt-3 flex items-center justify-center gap-2 text-sm text-green-700">
            <Check className="h-4 w-4" aria-hidden="true" />
            Nahráno: {upload.fileName}
          </p>
        )}
        {upload.status === 'cancelled' && (
          <p role="status" className="mt-3 text-center text-sm text-gray-600">
            Nahrávání zrušeno.
          </p>
        )}
      </div>
    </div>
  );
};

export default FileUploader;
